import 'CssFolder/accordion-list.scss';
import { EmptyState, Icon, Icons, Intent } from "construct-ui";
import moment from "moment";
import DatePickerComponent from "../../../components/date-picker/date-picker.component";
import MultiSelectComponent from "../../../components/multi-select/multi-select.component";
import NumberInputComponent from "../../../components/number/number-input.component";
import PhotoInputComponent from "../../../components/photo/photo-input.component";
import RatingInputComponent from "../../../components/rating/rating-input.component";
import SingleSelectComponent from "../../../components/single-select/single-select.component";
import SwitchInputComponent from "../../../components/switch/switch-input.component";
import { SURVEY_QUESTION_TYPE_CODES, formats } from "../../../config/constants";
import HeaderPublicLayout from "../../../layout/header-public.layout";
import surveyModel from "../../../models/survey/survey.model";
import MithrilMarkdownParser from '../../../utils/Markdown/markdown-mithril-parser';
import ViewBase from "../../view-base.view";


/**
 * Public Survey View
 * 
 * Receives the guid from URL to query for the survey details and display it
 * 
 * @author Darrus Goh 
 */
export default function PublicSurveyView() {
    ViewBase.call(this);

    const FORM_DATA_SURVEY_KEY = 'Survey';
    const FORM_DATA_FILES_KEY = 'Images';

    let survey = null;
    let siteId = '';
    let submitted = false;
    let surveyDisplay = null;
    let responses = {};

    const getSurveyQuestions = (guid) => {
        this.showLoading();
        surveyModel.getQuestionsPublic(guid)
            .then(results => {
                survey = results[0];
                updateSurveyDisplay();
            })
            .finally(() => {
                this.hideLoading();
            });
    };

    const updateSurveyDisplay = () => {
        const BLANK_CATEGORY = '<BLANK>';
        let blankCount = 0;

        surveyDisplay = structuredClone(survey);

        surveyDisplay.surveyCategoryList.forEach(category => {
            category.id = category.name === BLANK_CATEGORY
                ? `${category.name.slice(1, category.name.length - 1)}${blankCount}` :
                category.name.replaceAll(' ', '');

            category.surveySubCategoryList.forEach(subCategory => {
                let questionIndex = 0;
                while (questionIndex !== subCategory.surveyQuestionsList.length) {
                    const question = subCategory.surveyQuestionsList[questionIndex];

                    // If question has parent check if parent option is selected
                    if (question.parentQuestionID !== null) {
                        let questionResponse = responses[question.parentQuestionID];

                        if (!_.isEmpty(questionResponse)) {
                            if (Array.isArray(questionResponse)) {
                                if (!questionResponse.find((answer) => answer.code == question.parentAnswerID)) {
                                    subCategory.surveyQuestionsList.splice(questionIndex, 1);
                                    continue;
                                }
                            } else if (questionResponse.code != question.parentAnswerID) {
                                subCategory.surveyQuestionsList.splice(questionIndex, 1);
                                continue;
                            }
                        } else {
                            subCategory.surveyQuestionsList.splice(questionIndex, 1);
                            continue;
                        }
                    }

                    questionIndex++;
                }
            })
        });
    }

    const onSubmit = (publicSurveyGuid) => (e) => {
        // Form validation
        if (!this.validateForm(e)) {
            findAndNavigateToInvalidInput();
            return false;
        }

        const activeQuestions = surveyDisplay.surveyCategoryList
            .flatMap(category => category.surveySubCategoryList
                .flatMap(subCategory => subCategory.surveyQuestionsList));
        const formData = buildFormData(activeQuestions);

        if (formData.getAll(FORM_DATA_SURVEY_KEY).length === 0 && formData.getAll(FORM_DATA_FILES_KEY).length === 0) {
            this.showErrorMessage(this.getText('EM00000790', 'Please fill in the survey'), this.getText('LT00000980', 'Empty Survey'));
            return;
        }

        this.showLoading();
        surveyModel.submitSurveyPublic(publicSurveyGuid, formData)
            .then(() => { submitted = true; })
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => { this.hideLoading() });
    }

    const buildFormData = (activeQuestions) => {
        const formData = new FormData();
        let surveyFormResponses = [];
        formData.append("siteId", siteId);
        for (const question of activeQuestions) {
            if (responses.hasOwnProperty(question.surveyQuestionId)) {
                const questionResponse = responses[question.surveyQuestionId];

                if (questionResponse) {
                    const responseObjectArray = getResponseObjectArray(question, questionResponse)

                    // Special handling for photo objects
                    if (question.questionType === SURVEY_QUESTION_TYPE_CODES.PHOTO) {
                        let consolidatedFilenames = [];
                        let files = question.photoNumberLimit > 1 ? questionResponse : [questionResponse];

                        let fileCount = 1; // Allow files with same file name
                        for (const file of files) {
                            let newFilename = `${question.surveyQuestionId}_${fileCount}_${file.name}`;
                            consolidatedFilenames.push(file.name);

                            if (!formData.has(FORM_DATA_FILES_KEY)) {
                                formData.set(FORM_DATA_FILES_KEY, file, newFilename);
                            } else {
                                formData.append(FORM_DATA_FILES_KEY, file, newFilename);
                            }
                            fileCount++;
                        }

                        responseObjectArray[0].ResponseText = consolidatedFilenames.join('^');
                    }

                    surveyFormResponses = surveyFormResponses.concat(responseObjectArray);
                }
            }
        }

        if (surveyFormResponses.length > 0) {
            formData.set(FORM_DATA_SURVEY_KEY, JSON.stringify(surveyFormResponses));
        }

        return formData;
    }

    const getResponseObjectArray = (question, response) => {
        const SURVEY_OPTION_ID = 'SurveyOptionId';
        const RESPONSE_TEXT = 'ResponseText';

        let responseObject = {
            SurveyQuestionId: question.surveyQuestionId.toString(),
            [SURVEY_OPTION_ID]: '0',
            [RESPONSE_TEXT]: '',
            ClientDateTime: '',
            ACTION: 'A'
        };

        if (question.questionType === SURVEY_QUESTION_TYPE_CODES.MULTIPLE ||
            question.questionType === SURVEY_QUESTION_TYPE_CODES.MULTI_DROPDOWN) {
            let multiResponseArray = [];
            for (const option of response) {
                let multiResponseObject = structuredClone(responseObject);
                multiResponseObject[SURVEY_OPTION_ID] = option.code.toString();
                multiResponseObject[RESPONSE_TEXT] = option.name;
                multiResponseArray.push(multiResponseObject);
            }
            return multiResponseArray;
        } else if (question.questionType === SURVEY_QUESTION_TYPE_CODES.SINGLE ||
            question.questionType === SURVEY_QUESTION_TYPE_CODES.SINGLE_DROPDOWN) {
            responseObject[SURVEY_OPTION_ID] = response.code.toString();
            responseObject[RESPONSE_TEXT] = response.name;
        } else if (question.questionType !== SURVEY_QUESTION_TYPE_CODES.PHOTO) {
            responseObject[RESPONSE_TEXT] = response.toString();
        }

        return [responseObject];
    }

    const findAndNavigateToInvalidInput = () => {
        // Expand collapsed category
        const accordionWithInvalid = document.querySelector('.accordion-item:has(input:invalid)');
        const collapseButton = accordionWithInvalid?.querySelector('.collapsed');

        if (collapseButton) {
            collapseButton.click();
            const accordionBody = accordionWithInvalid.querySelector('.accordion-collapse');
            accordionBody.addEventListener('transitionend', () => { scrollToInvalidInput(accordionWithInvalid) }, { once: true });
        } else {
            scrollToInvalidInput(document);
        }
    }

    const scrollToInvalidInput = (element) => {
        let invalidInput = element.querySelector('input:invalid');

        if (invalidInput.style['display'] === 'none') {
            // For Multi Select & Single Select with hidden input
            invalidInput = invalidInput.previousElementSibling;
        }

        invalidInput.scrollIntoView({ behavior: 'instant', block: 'center' });
    }

    const subCategoriesComponent = {
        view: ({ attrs: { subCategories } }) => {
            return subCategories.map(subCategory => m('div',
                subCategory.name && m('h3.text-decoration-underline.mb-3', subCategory.name),
                m('.vstack.gap-4',
                    subCategory.surveyQuestionsList.map(question => m('.row.gap-2',
                        m('.col-12',
                            m(`label${question.mandatoryYN === 'Y' ? '.mandatory-before' : ''}`, MithrilMarkdownParser.parse(question.questionText)),
                            question.questionHint !== '' && m('p.text-muted.text-wrap.m-0', { style: { lineHeight: '1rem' } }, MithrilMarkdownParser.parse(question.questionHint))
                        ),
                        question.photo !== null && m('.col-12',
                            m('img.img-fluid', { src: question.photo })
                        ),
                        m('.col-12.position-relative',
                            m(inputComponent, {
                                question,
                                value: responses[question.surveyQuestionId],
                                event: (value) => {
                                    responses[question.surveyQuestionId] = value;
                                    updateSurveyDisplay();
                                }
                            }),
                            m('.invalid-tooltip', this.getText('LT00000729', 'This field is required'))
                        )
                    ))
                ))
            );
        }
    }

    const inputComponent = {
        view: ({ attrs: { question, value, event } }) => {
            // Prepare options for dropdown usage
            let dropdownOptions = question.optionList.map(option => { return { code: option.surveyOptionId, name: option.optionText } });
            let required = question.mandatoryYN === 'Y';

            switch (question.questionType) {
                case SURVEY_QUESTION_TYPE_CODES.TEXT:
                    return m('input', {
                        id: question.surveyQuestionId,
                        class: 'form-control',
                        required,
                        type: 'text',
                        value,
                        onchange: (e) => { event(e.target.value) }
                    });
                case SURVEY_QUESTION_TYPE_CODES.SINGLE_DROPDOWN:
                case SURVEY_QUESTION_TYPE_CODES.SINGLE:
                    return m(SingleSelectComponent, {
                        id: question.surveyQuestionId,
                        required,
                        items: dropdownOptions,
                        selectedItem: value,
                        filterable: false,
                        onSelect: (selectedItem) => {
                            event(selectedItem);
                        }
                    })
                case SURVEY_QUESTION_TYPE_CODES.MULTI_DROPDOWN:
                case SURVEY_QUESTION_TYPE_CODES.MULTIPLE:
                    return m(MultiSelectComponent, {
                        id: question.surveyQuestionId,
                        required,
                        items: dropdownOptions,
                        selectedItems: value,
                        filterable: false,
                        onSelect: (selectedItems) => {
                            event(selectedItems);
                        }
                    });
                case SURVEY_QUESTION_TYPE_CODES.DATE:
                    return m(DatePickerComponent, {
                        id: question.surveyQuestionId,
                        class: 'form-control',
                        required,
                        date: value ? moment(value).format(formats.dateFormat) : value,
                        onselect: (e) => {
                            const newDate = e.detail.date;
                            if (newDate) {
                                event(moment(newDate).format(formats.apiDateFormat))
                            } else {
                                event(newDate);
                            }
                        }
                    });
                case SURVEY_QUESTION_TYPE_CODES.RATING:
                    return m(RatingInputComponent, {
                        id: question.surveyQuestionId,
                        value,
                        required,
                        size: 5,
                        minText: question.ratingWordsMin,
                        maxText: question.ratingWordsMax,
                        onchange: event
                    });
                case SURVEY_QUESTION_TYPE_CODES.NUMBER:
                    return m(NumberInputComponent, {
                        id: question.surveyQuestionId,
                        value,
                        required,
                        step: '.001',
                        min: question.minValue,
                        max: question.maxValue,
                        onChange: event
                    });
                case SURVEY_QUESTION_TYPE_CODES.YES_NO: {
                    const switchFlag = value === 'Y';

                    // required attribute is not passed in as false is a valid answer
                    return m(SwitchInputComponent, {
                        value: switchFlag,
                        label: switchFlag ? this.getText('LT00000023', 'Yes') : this.getText('LT00000024', 'No'),
                        onchange: (result) => { event(result ? 'Y' : 'N'); }
                    })
                }
                case SURVEY_QUESTION_TYPE_CODES.PHOTO:
                    return m(PhotoInputComponent, {
                        id: question.surveyQuestionId,
                        photoCountLimit: question.photoNumberLimit,
                        onInput: event,
                        required
                    });
                default:
                    return m('input', {
                        class: 'form-control form-control-bg',
                        type: 'text',
                        disabled: true,
                        readonly: true,
                        placeholder: 'Unsupported input type.'
                    });
            }
        }
    }

    return {
        oninit: ({ attrs: { publicSurveyGuid } }) => {
            siteId = m.route.param("siteid") ?? '';
            getSurveyQuestions(publicSurveyGuid);
        },
        view: ({ attrs: { publicSurveyGuid } }) => {
            return [
                m(HeaderPublicLayout, { displayLogin: false }),
                m('.container-fluid', { style: { position: 'relative', top: 'var(--header-height)', 'min-height': 'calc(100vh - var(--header-height)' } },
                    m('.container-md.container-size-md.py-3',
                        (survey != null && !submitted)
                            ? m('.card',
                                m('.card-header',
                                    m('h1.card-title', survey.title),
                                    m('p.card-text', survey.description)
                                ),
                                m('form.card-body.px-4.needs-validation[novalidate]', { onsubmit: onSubmit(publicSurveyGuid) },
                                    m('.accordion.accordion-list',
                                        surveyDisplay.surveyCategoryList.map(category => m('.accordion-item',
                                            m('.accordion-header',
                                                m('button.accordion-button.px-0', {
                                                    type: 'button',
                                                    'data-bs-toggle': 'collapse',
                                                    'data-bs-target': `#${category.id}`,
                                                    'aria-expanded': false,
                                                    'aria-controls': category.id
                                                },
                                                    m('h2.m-0', category.name)
                                                )
                                            ),
                                            m(`#${category.id}.accordion-collapse.collapse.show[aria-labelledby=${category.id}]`,
                                                m('.accordion-body.px-2',
                                                    m('.vstack.gap-5',
                                                        m(subCategoriesComponent, { subCategories: category.surveySubCategoryList })
                                                    )
                                                )
                                            )
                                        )
                                        )
                                    ),
                                    m('.hstack.w-100.justify-content-center.mt-4',
                                        m('button.btn.btn-primary[type=submit]', 'Submit')
                                    )
                                )
                            )
                            : !this._isLoading && submitted
                                ? m(EmptyState, {
                                    icon: m(Icon, { name: Icons.CHECK, intent: Intent.POSITIVE }),
                                    header: m('p.text-wrap.text-center',
                                        this.getText('LT00000986', 'Your response has been successfully submitted.'),
                                        m('br'),
                                        this.getText('LT00000987', 'Thank you for your time.'),
                                    ),
                                    fill: true
                                })
                                : m(EmptyState, {
                                    icon: m(Icon, { name: Icons.ALERT_TRIANGLE, intent: Intent.NEGATIVE }),
                                    header: m('p.text-wrap.text-center', this.getText('EM00000792', 'The requested survey is either expired or does not exist.')),
                                    fill: true
                                })
                    ),
                    this.getBaseViews()
                )
            ]
        }
    };
}